/**
 * BottomSection.js - 캘린더 하단 섹션 컴포넌트
 *
 * 📍 위치: calendarView/components/BottomSection.js
 * 🔗 연결: ../../MobileCalendarView.js
 */

import React from 'react';
import SimplifiedScheduleDisplay from '../../SimplifiedScheduleDisplay';
import { getEventsForDate } from '../utils/eventUtils';

const BottomSection = ({ isEditing, calendarView, selectedDate, defaultSchedule, personalTimes, events, onSplitItemClick, displayMode }) => {
   // 상세형 모드가 아니면 렌더링 안 함
   if (displayMode && displayMode !== 'detail') return null;

   // 1. 편집 모드일 때: '일정 관리' 섹션 표시
   if (isEditing) {
      if (calendarView === 'dayGridMonth') {
         return (
            <div className="management-section">

               <div className="sections-container">
                  <div className="preference-section"><h4 className="subsection-title">선호시간</h4><p className="section-description">클릭 또는 챗봇으로 추가한 가능한 시간들 (자동배정 시 사용됨)</p><SimplifiedScheduleDisplay schedule={defaultSchedule} type="preference" /></div>
                  <div className="personal-section"><h4 className="subsection-title">개인시간</h4><p className="section-description">자동 스케줄링 시 이 시간들은 제외됩니다</p><SimplifiedScheduleDisplay schedule={personalTimes} type="personal" /></div>
               </div>
            </div>
         );
      }
      return null;
   }

   // 2. 기본 상태 (모든 뷰): 하단에 선택된 날짜(또는 오늘)의 일정 리스트 표시
   const targetDate = selectedDate || new Date();

   const allDayEvents = getEventsForDate(targetDate, events)
      .sort((a, b) => new Date(a.start) - new Date(b.start));

   const memos = allDayEvents.filter(e => e.isMemo);
   const schedules = allDayEvents.filter(e => !e.isMemo);

   // 메모 렌더 컴포넌트 (재사용)
   const MemoItem = ({ memo, idx }) => (
      <div
         key={`memo-${idx}`}
         style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 6px',
            marginTop: '4px',
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400e',
         }}
      >
         <span>📝</span>
         <span>{memo.title.replace(/^📝\s*/, '')}</span>
      </div>
   );

   return (
      <div className="split-view-list" style={calendarView === 'dayGridMonth' ? { height: '40%', borderTop: '1px solid #e5e7eb' } : {}}>
         <div className="split-list-header">
            {targetDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' })}
         </div>

         {allDayEvents.length === 0 ? (
            <div className="split-no-events">일정이 없습니다</div>
         ) : (
            <div className="split-list-scroll-area">
               {schedules.length > 0 ? (
                  /* 일정이 있을 때: 일정 카드 아래에 메모를 인라인 표시 */
                  <>
                     {schedules.map((event, idx) => (
                        <div key={idx} style={{ marginBottom: '6px' }}>
                           <div
                              className="split-list-item"
                              onClick={() => onSplitItemClick(event)}
                              style={{ cursor: 'pointer', marginBottom: 0 }}
                           >
                              <div className="split-item-time">
                                 {new Date(event.start).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                 <br />~ {new Date(event.end).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                              </div>
                              <div style={{ flex: 1 }}>
                                 <div
                                    className="split-item-content"
                                    style={{ backgroundColor: event.backgroundColor || '#3b82f6' }}
                                 >
                                    {event.title === '가능' ? '선호시간' : event.title}
                                 </div>
                                 {/* 일정에 붙은 note 표시 */}
                                 {event.note && (
                                    <div style={{
                                       display: 'flex', alignItems: 'center', gap: '4px',
                                       padding: '3px 8px', marginTop: '3px',
                                       backgroundColor: '#fef3c7', border: '1px solid #fcd34d',
                                       borderRadius: '6px', fontSize: '11px', color: '#92400e'
                                    }}>
                                       <span>📝</span><span>{event.note}</span>
                                    </div>
                                 )}
                                 {/* 같은 날 독립 메모를 마지막 일정 카드 아래에만 표시 */}
                                 {idx === schedules.length - 1 && memos.map((memo, mIdx) => (
                                    <MemoItem key={mIdx} memo={memo} idx={mIdx} />
                                 ))}
                              </div>
                           </div>
                        </div>
                     ))}
                  </>
               ) : (
                  /* 메모만 있을 때: 기존 방식대로 상단에 표시 */
                  <div style={{ marginBottom: '6px' }}>
                     {memos.map((memo, idx) => (
                        <div
                           key={`memo-${idx}`}
                           className="split-list-item"
                           style={{ cursor: 'default', alignItems: 'center' }}
                        >
                           <div className="split-item-time" style={{ color: '#92400e', fontSize: '12px' }}>
                              📝 메모
                           </div>
                           <div
                              className="split-item-content"
                              style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
                           >
                              {memo.title.replace(/^📝\s*/, '')}
                           </div>
                        </div>
                     ))}
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

export default BottomSection;
